import React, { useEffect, useMemo, useState } from 'react';

import { CloseOutlined, PlusOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { schemas } from '@recurrency/core-api-schema';
import {
  ErpRole,
  ErpRoleForeignIdType,
  IntegratedErps,
  TenantIntegrationStatus,
  TenantProductName,
  RecurrencyRole,
  TenantUserPermission,
} from '@recurrency/core-api-schema/dist/common/enums';
import { TenantUserErpRolePayload } from '@recurrency/core-api-schema/dist/tenants/postCreateTenantUser';
import { PutUpdateTenantUserBodyParams } from '@recurrency/core-api-schema/dist/tenants/putUpdateTenantUser';
import { TenantUserDTO } from '@recurrency/core-api-schema/dist/tenants/tenantUserDTO';
import { ErpRoleMetadataMap, RecurrencyRoleMetadataMap } from '@recurrency/core-api-schema/dist/users/common';
import { Form, FormInstance, notification } from 'antd';
import { ColumnType } from 'antd/lib/table';
import { colors } from 'theme/colors';
import { fontWeights } from 'theme/typography';

import { AsyncSelect } from 'components/AsyncSelect';
import { useSalesRepsSelectProps } from 'components/AsyncSelect/useAsyncSelectProps';
import { Badge } from 'components/Badge';
import { Button } from 'components/Button';
import { DividerLine } from 'components/DividerLine';
import { CheckboxFormItem } from 'components/FormItems';
import { Input } from 'components/Input';
import { ExternalLink } from 'components/Links';
import { SmallLoader } from 'components/Loaders';
import { Modal } from 'components/Modal';
import { ModalSectionTitle } from 'components/Modal/ModalSectionTitle';
import { LabelWithDescription } from 'components/recipes/select/LabelWithDescription';
import { Select } from 'components/Select';
import { Table } from 'components/Table';
import { Tooltip } from 'components/Tooltip';

import { useGlobalApp } from 'hooks/useGlobalApp';

import { coreApiFetch } from 'utils/api';
import { captureAndShowError } from 'utils/error';
import { splitIfIdNameStr } from 'utils/formatting';
import { isEqual } from 'utils/object';
import { arePermissionsEnabled, hasUserPermission, shouldShowProduct } from 'utils/roleAndTenant';
import { asKeyOf } from 'utils/tables';

import {
  PermissionsSection,
  confirmSaveWithoutPermissions,
  permissionFormValues,
  storedPermissions,
} from './PermissionsSection';
import { UserPermissionsFields } from './types';
import { UserCompaniesSelect } from './UserCompaniesSelect';

const defaultErpRole = { name: ErpRole.Admin, foreignId: undefined };

/**
 * Categories used to group Recurrency role permissions in the user management modal.
 * The core-api-schema metadata does not define a category, so we map them here.
 */
enum RecurrencyRoleCategory {
  Administration = 'Administration',
  Sales = 'Sales',
  Payments = 'Payments',
  AiTools = 'AI Tools',
}

/** Order in which permission categories are displayed. */
const recurrencyRoleCategoryOrder: RecurrencyRoleCategory[] = [
  RecurrencyRoleCategory.Administration,
  RecurrencyRoleCategory.Sales,
  RecurrencyRoleCategory.Payments,
  RecurrencyRoleCategory.AiTools,
];

const recurrencyRoleCategoryMap: Record<RecurrencyRole, RecurrencyRoleCategory> = {
  [RecurrencyRole.TenantAdmin]: RecurrencyRoleCategory.Administration,
  [RecurrencyRole.PlanningAdmin]: RecurrencyRoleCategory.Administration,
  [RecurrencyRole.CustomerExperienceRepresentative]: RecurrencyRoleCategory.Sales,
  [RecurrencyRole.CounterSalesRepresentative]: RecurrencyRoleCategory.Sales,
  [RecurrencyRole.PriceAndGrossMarginManager]: RecurrencyRoleCategory.Sales,
  [RecurrencyRole.ReportsManager]: RecurrencyRoleCategory.Sales,
  [RecurrencyRole.InventoryManager]: RecurrencyRoleCategory.Sales,
  [RecurrencyRole.SalesOrderManager]: RecurrencyRoleCategory.Sales,
  [RecurrencyRole.PaymentsModuleViewer]: RecurrencyRoleCategory.Payments,
  [RecurrencyRole.PaymentsChargeCreator]: RecurrencyRoleCategory.Payments,
  [RecurrencyRole.PaymentsRefundCreator]: RecurrencyRoleCategory.Payments,
  [RecurrencyRole.McpRead]: RecurrencyRoleCategory.AiTools,
  [RecurrencyRole.McpWrite]: RecurrencyRoleCategory.AiTools,
  [RecurrencyRole.ChatWithData]: RecurrencyRoleCategory.AiTools,
};

export const convertUserPermissionsForSubmit = (fields: UserPermissionsFields): PutUpdateTenantUserBodyParams => {
  // Aggregate recurrency roles that are true into final array. Roles removed from the enum since the user was
  // last saved are dropped here because the endpoint rejects them.
  const recurrencyRoles = Object.entries(fields.recurrencyRoles)
    .filter(([role, value]) => value && Object.values(RecurrencyRole).includes(role as RecurrencyRole))
    .map(([role]) => ({
      name: role as RecurrencyRole,
    }));

  // Clear allowedCompanyIds if company restriction is not enabled
  if (!fields.isCompanyRestricted) {
    fields.allowedCompanyIds = [];
  }

  // Deduplicate erp roles and format foreignIds
  const allRoles = fields.roles
    ? fields.roles.map((role) => {
        const { name, foreignId } = role;
        const foreignIdStrings = Array.isArray(foreignId) ? foreignId : [foreignId ?? ''];
        const foreignIds = foreignIdStrings.map((id) => splitIfIdNameStr(id)?.foreignId ?? id);
        if (
          ErpRoleMetadataMap[role.name].foreignIdType === ErpRoleForeignIdType.SingleRep ||
          ErpRoleMetadataMap[role.name].foreignIdType === ErpRoleForeignIdType.Text
        ) {
          return { name, foreignId: foreignIds[0] };
        }
        if (ErpRoleMetadataMap[role.name].foreignIdType === ErpRoleForeignIdType.MultipleRep) {
          return { name, foreignId: foreignIds.join(',') };
        }
        return { name, foreignId: '' };
      })
    : [defaultErpRole];
  const deduplicatedRoles = [
    ...new Map<string, TenantUserErpRolePayload>(allRoles.map((r) => [`${r.name}:${r.foreignId}`, r])).values(),
  ];

  return {
    isActive: true,
    roles: deduplicatedRoles,
    recurrencyRoles,
    allowedCompanyIds: fields.allowedCompanyIds,
    // omitted while the tenant still gates by roles, so the endpoint leaves stored permissions alone
    ...(arePermissionsEnabled() ? { permissions: storedPermissions(fields.permissions) } : {}),
  };
};

const UserPermissionsInternalBadge = () => {
  const { activeUser } = useGlobalApp();
  return activeUser.isRecurrencyInternalUser ? (
    <Tooltip
      title="Only visible to Recurrency internal users, unless enabled for a specific user. If a user is assigned an internal role or permission, admins for that tenant can see and modify that role or permission."
      placement="right"
    >
      <Badge variant="danger" label="Internal" size="small" />
    </Tooltip>
  ) : (
    <Tooltip
      title="This role or permission is managed by the Recurrency team. To assign it to additional users, contact support."
      placement="right"
    >
      <Badge variant="info" label="Managed" size="small" />
    </Tooltip>
  );
};

const sectionClassName = css`
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid ${colors.neutral[200]};
`;

const sectionTitleClassName = css`
  font-weight: ${fontWeights.medium};
  color: ${colors.neutral[600]};
  margin-bottom: 4px;
`;

export function UserPermissionsModal({
  tenantUser,
  onClose,
}: {
  tenantUser: TenantUserDTO;
  onClose: (shouldReload?: boolean) => void;
}) {
  const [form] = Form.useForm<UserPermissionsFields>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const handleUpdatePermissions = async () => {
    try {
      await form.validateFields();
    } catch (err) {
      notification.error({ message: 'Please resolve all validation errors and then resubmit.' });
      return;
    }
    // Get all values rather than just ones in the UI. This way we don't delete permissions/roles
    // if they temporarily lose access to a product
    const fields = form.getFieldsValue(true);
    if (arePermissionsEnabled() && !(await confirmSaveWithoutPermissions(fields.permissions))) {
      return;
    }
    setIsSubmitting(true);
    try {
      const filledFields = { ...fields, roles: fields.roles ?? tenantUser.roles };
      await coreApiFetch(schemas.tenants.putUpdateTenantUser, {
        bodyParams: convertUserPermissionsForSubmit(filledFields),
        pathParams: { tenantId: tenantUser.tenantId, userId: tenantUser.userId },
      });
      notification.success({ message: `Updated permissions for ${tenantUser.user.email}` });
      onClose(true);
    } catch (err) {
      captureAndShowError(err, `Error while updating user permissions. Please try again.`);
    }
    setIsSubmitting(false);
  };

  return (
    <Modal
      visible
      width={arePermissionsEnabled() ? 800 : undefined}
      onCancel={() => onClose()}
      title={`Manage Access (${tenantUser.user.email})`}
      onOk={handleUpdatePermissions}
      okText="Save Changes"
      okButtonProps={{ disabled: isLoading || (arePermissionsEnabled() && !isDirty) }}
      confirmLoading={isSubmitting}
    >
      <UserPermissionsForm
        tenantUser={tenantUser}
        form={form}
        onDirtyChange={setIsDirty}
        onLoadingChange={setIsLoading}
      />
    </Modal>
  );
}

export function UserPermissionsForm({
  form,
  tenantUser,
  onDirtyChange,
  onLoadingChange,
}: {
  form: FormInstance;
  tenantUser?: TenantUserDTO;
  /** Reports after every edit whether saving would change anything. */
  onDirtyChange?: (isDirty: boolean) => void;
  /** Reports whether the form is still waiting on the sales reps the ERP roles need. */
  onLoadingChange?: (isLoading: boolean) => void;
}) {
  const { activeTenant, activeUser } = useGlobalApp();

  // Only show ERP role management for Sales tenants, DPPA tenants use Admin for all users
  const showErpRoles = shouldShowProduct(activeTenant, TenantProductName.SalesSearchAndLookup);
  const salesRepsSelectProps = useSalesRepsSelectProps({ skip: !showErpRoles });

  const initialValues = useMemo(
    () => ({
      recurrencyRoles: Object.fromEntries(tenantUser?.recurrencyRoles.map((role) => [role.name, true]) ?? []),
      permissions: permissionFormValues(tenantUser?.permissions),
      isCompanyRestricted: (tenantUser?.allowedCompanyIds?.length ?? 0) > 0,
      allowedCompanyIds: tenantUser?.allowedCompanyIds ?? [],
      roles:
        tenantUser && tenantUser.roles.length > 0
          ? tenantUser.roles.map((role) => {
              if (ErpRoleMetadataMap[role.name].foreignIdType === ErpRoleForeignIdType.Text) {
                return role;
              }
              const foreignIds = role.foreignId.split(',');
              // Convert from foreignId to string including foreignId and sales rep name
              const foreignIdStrings = foreignIds.map((id) =>
                id ? salesRepsSelectProps.options.find((o) => o.value.startsWith(`${id}:`))?.value ?? id : undefined,
              );
              return {
                ...role,
                foreignId:
                  ErpRoleMetadataMap[role.name].foreignIdType === ErpRoleForeignIdType.MultipleRep
                    ? foreignIdStrings
                    : foreignIdStrings[0],
              };
            })
          : [defaultErpRole],
    }),
    [tenantUser, salesRepsSelectProps.options],
  );

  // Mirroring antd form values for the sake of accurate DOM updates for conditional fields, see https://github.com/ant-design/ant-design/issues/21829
  const [formValues, setFormValues] = useState<UserPermissionsFields>(initialValues);

  const updateFormValues = (values: UserPermissionsFields) => {
    // permissions are not form fields, so the mounted values are merged over the mirror rather than replacing it
    setFormValues({ ...formValues, ...values });
    // the payloads are compared rather than the raw values, so a box toggled back off does not count as a change
    onDirtyChange?.(
      !isEqual(
        convertUserPermissionsForSubmit({ ...form.getFieldsValue(true) }),
        convertUserPermissionsForSubmit({ ...initialValues }),
      ),
    );
  };

  const setValue = (key: string, value: unknown) => {
    form.setFieldsValue({ [key]: value });
    updateFormValues({ ...formValues, [key]: value });
  };

  // the form mounts before the sales reps arrive, so the rep-labelled role values are pushed in once they do
  useEffect(() => {
    onLoadingChange?.(salesRepsSelectProps.isLoading);
    if (!salesRepsSelectProps.isLoading) {
      setValue(asKeyOf<UserPermissionsFields>('roles'), initialValues.roles);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salesRepsSelectProps.isLoading]);

  const columns: ColumnType<TenantUserErpRolePayload>[] = [
    {
      title: '',
      dataIndex: asKeyOf<TenantUserErpRolePayload>('name'),
      width: '30px',
      render: (_name, _role, i) => (
        <Button
          className={css`
            margin: 8px 0;
          `}
          disabled={formValues.roles?.length === 1}
          danger
          type="text"
          size="small"
          onClick={() => {
            const roles = form.getFieldValue('roles');
            setValue('roles', roles.slice(0, i).concat(roles.slice(i + 1, roles.length)));
          }}
        >
          <CloseOutlined />
        </Button>
      ),
    },
    {
      title: 'Role',
      dataIndex: asKeyOf<TenantUserErpRolePayload>('name'),
      render: (name, _role, i) => (
        <Form.Item
          className={css`
            width: 200px;
            margin-bottom: 0;
          `}
          name={[asKeyOf<UserPermissionsFields>('roles'), i, asKeyOf<TenantUserErpRolePayload>('name')]}
        >
          <Select
            options={Object.entries(ErpRoleMetadataMap)
              .filter(([erpRole, erpRoleMetadata]) =>
                // Show internal fields if the user is internal or that role has been assigned
                erpRoleMetadata.internalOnly ? activeUser.isRecurrencyInternalUser || erpRole === name : true,
              )
              .map(([erpRole, erpRoleMetadata]) => ({
                value: erpRole,
                label: (
                  <LabelWithDescription
                    title={
                      <>
                        {erpRoleMetadata.name}
                        {activeUser.isRecurrencyAdmin && (
                          <span
                            className={css`
                              color: ${colors.neutral[500]};
                            `}
                          >
                            {' '}
                            ({erpRole})
                          </span>
                        )}
                      </>
                    }
                    description={erpRoleMetadata.description}
                    badge={erpRoleMetadata.internalOnly ? <UserPermissionsInternalBadge /> : undefined}
                  />
                ),
              }))}
          />
        </Form.Item>
      ),
    },
    {
      title: 'Rep Code(s)',
      dataIndex: asKeyOf<TenantUserErpRolePayload>('name'),
      width: '100%',
      render: (_name, role, i) =>
        ErpRoleMetadataMap[role.name].foreignIdType === ErpRoleForeignIdType.None ? (
          <Select
            disabled
            placeholder="Admins see all reps"
            className={css`
              width: 100%;
            `}
          />
        ) : (
          <Form.Item
            required
            className={css`
              margin-bottom: 0;
            `}
            name={[asKeyOf<UserPermissionsFields>('roles'), i, asKeyOf<TenantUserErpRolePayload>('foreignId')]}
            rules={[{ required: true, message: 'Select a sales rep code' }]}
          >
            {ErpRoleMetadataMap[role.name].foreignIdType === ErpRoleForeignIdType.Text ? (
              // In future, this may hold foreign ids other than rep code such as buyer code
              <Input placeholder="Enter a rep code" />
            ) : (
              <AsyncSelect
                defaultValue={[]}
                selectProps={salesRepsSelectProps}
                entityPlural="sales reps"
                mode={
                  ErpRoleMetadataMap[role.name].foreignIdType === ErpRoleForeignIdType.MultipleRep
                    ? 'multiple'
                    : undefined
                }
              />
            )}
          </Form.Item>
        ),
    },
  ];

  // an editor can only hand out settings access they hold themselves; hidden rows still round-trip on save
  const canGrantSettings =
    hasUserPermission(activeTenant, TenantUserPermission.AccessSettingsGeneralView) &&
    hasUserPermission(activeTenant, TenantUserPermission.ActionEditSettings);

  const isRecurrencyRoleVisible = (role: RecurrencyRole) => {
    const roleMetadata = RecurrencyRoleMetadataMap[role];
    return !(
      (roleMetadata.internalOnly && !activeUser.isRecurrencyInternalUser && !initialValues.recurrencyRoles[role]) ||
      (roleMetadata.products && !roleMetadata.products.some((product) => shouldShowProduct(activeTenant, product)))
    );
  };

  const renderRecurrencyRoleCheckbox = (role: RecurrencyRole) => {
    const roleMetadata = RecurrencyRoleMetadataMap[role];
    return (
      <CheckboxFormItem
        key={role}
        name={[asKeyOf<UserPermissionsFields>('recurrencyRoles'), role]}
        className={css`
          margin-bottom: 0;
        `}
        checkboxLabel={
          <>
            {roleMetadata.description}
            {roleMetadata.internalOnly && (
              <>
                {' '}
                <UserPermissionsInternalBadge />
              </>
            )}
            {activeUser.isRecurrencyAdmin && (
              <span
                className={css`
                  color: ${colors.neutral[500]};
                `}
              >
                {' '}
                ({role})
              </span>
            )}
          </>
        }
      />
    );
  };

  const erpRolesTitle = (
    <>
      Roles{' '}
      <Tooltip
        placement="right"
        title={
          <div>
            Determines which set of orders, invoices, and customers a user can access. Note that each user can be
            assigned to multiple roles, with the ability toggle between them in the top left dropdown of their
            Recurrency page. Learn more about roles{' '}
            <ExternalLink to="https://help.recurrency.com/hc/en-us/articles/25536329954971-Self-Serve-User-Management#h_01HXAD4N8BMWZA8Z9WJ41M6VAM">
              here
            </ExternalLink>
            .
          </div>
        }
      >
        <QuestionCircleOutlined />
      </Tooltip>
    </>
  );
  const erpRolesTable = salesRepsSelectProps.isLoading ? (
    <SmallLoader />
  ) : (
    <div>
      <Table data={formValues.roles ?? []} columns={columns} />
      <Button
        type="link"
        onClick={() => {
          const roles = form.getFieldValue('roles');
          setValue('roles', (roles || []).concat(defaultErpRole));
        }}
      >
        <PlusOutlined />
        Add Role
      </Button>
    </div>
  );

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={initialValues}
      onValuesChange={(_, values) => updateFormValues(values)}
    >
      {showErpRoles && !arePermissionsEnabled() && (
        <div
          className={css`
            display: flex;
            flex-direction: column;
            gap: 8px;
            margin-bottom: 24px;
          `}
        >
          <ModalSectionTitle title={erpRolesTitle} />
          {erpRolesTable}
          <DividerLine marginTop={10} marginBottom={0} />
        </div>
      )}
      <div
        className={css`
          display: flex;
          flex-direction: column;
          gap: 8px;
        `}
      >
        <ModalSectionTitle
          title={
            <>
              Permissions{' '}
              <Tooltip
                placement="right"
                title={
                  <div>
                    Control access to specific pages and features. Learn more about permissions{' '}
                    <ExternalLink to="https://help.recurrency.com/hc/en-us/articles/25536329954971-Self-Serve-User-Management#h_01HXAD4N8B3HBVEFVR683THY8Q">
                      here
                    </ExternalLink>
                    .
                  </div>
                }
              >
                <QuestionCircleOutlined />
              </Tooltip>
            </>
          }
        />
        <div>
          {arePermissionsEnabled() && (
            <>
              <PermissionsSection
                value={formValues.permissions ?? {}}
                onChange={(next) => setValue(asKeyOf<UserPermissionsFields>('permissions'), next)}
                tenant={activeTenant}
                canGrantSettings={canGrantSettings}
              />
              {showErpRoles && (
                <div className={sectionClassName}>
                  <div className={sectionTitleClassName}>{erpRolesTitle}</div>
                  {erpRolesTable}
                </div>
              )}
            </>
          )}
          {recurrencyRoleCategoryOrder.map((category) => {
            // converted roles stay on the row for now; the Actions above replace them once the tenant is on permissions
            if (
              [RecurrencyRoleCategory.Administration, RecurrencyRoleCategory.AiTools].includes(category) &&
              arePermissionsEnabled()
            ) {
              return null;
            }
            const visibleRoles = Object.values(RecurrencyRole).filter(
              (role) => recurrencyRoleCategoryMap[role] === category && isRecurrencyRoleVisible(role),
            );
            if (visibleRoles.length === 0) {
              return null;
            }
            return (
              <div key={category} className={sectionClassName}>
                <div className={sectionTitleClassName}>{category}</div>
                {visibleRoles.map((role) => renderRecurrencyRoleCheckbox(role))}
              </div>
            );
          })}
          {activeTenant.erpType === IntegratedErps.P21 &&
            [TenantIntegrationStatus.Active, TenantIntegrationStatus.Paused].includes(
              activeTenant.integrations?.[0]?.status,
            ) && (
              <div key="company-restriction">
                <div className={sectionTitleClassName}>Company Restriction</div>
                <UserCompaniesSelect formValues={formValues} setValue={setValue} />
              </div>
            )}
        </div>
      </div>
    </Form>
  );
}
